import React, { useCallback, useEffect, useState } from 'react';
import {
  Card, Table, Typography, Alert, Empty, Tag, Space, Input, Button,
  Row, Col, Statistic, Select, DatePicker, Tooltip,
  theme,
} from 'antd';
import {
  CalendarOutlined, SearchOutlined, ArrowLeftOutlined, ReloadOutlined,
  CheckCircleOutlined, ClockCircleOutlined, StopOutlined,
} from '@ant-design/icons';
import { fiscalAdminService } from '../../services/api';
import FichaFiscal from './FichaFiscal';

const { Title, Text } = Typography;

/**
 * Agenda mensual de declaraciones.
 *
 * Responde "qué me falta este mes", que no es lo mismo que "a quién atiendo".
 * Por eso está separada de la cartera y su fila es la EMPRESA: la declaración
 * cuelga del RFC, y un cliente con dos empresas son dos declaraciones.
 *
 * Todo el trabajo pesado —estado, conteos, filtro, paginación— lo resuelve el
 * servidor. Aquí no se recalcula nada: si el filtro se aplicara en el cliente,
 * solo alcanzaría la página visible.
 */
const ESTADOS = {
  declarada: { color: 'green', label: 'Declarada', icon: <CheckCircleOutlined /> },
  pendiente: { color: 'orange', label: 'Pendiente', icon: <ClockCircleOutlined /> },
  bloqueada: { color: 'red', label: 'Bloqueada', icon: <StopOutlined /> },
};

const MOTIVOS = {
  sin_efirma: {
    texto: 'Sin e.firma cargada',
    ayuda: 'Hay que cargar su e.firma antes de poder declarar por él.',
  },
  sin_consentimiento: {
    texto: 'Sin autorización',
    ayuda: 'El contribuyente no ha autorizado el uso de su e.firma. Se le envió un enlace por correo; hasta que responda, su e.firma no se puede exportar.',
  },
};

const Agenda = () => {
  const { token } = theme.useToken();
  const [datos, setDatos] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [seleccion, setSeleccion] = useState(null);

  const [periodo, setPeriodo] = useState(null);
  const [estado, setEstado] = useState(null);
  const [busqueda, setBusqueda] = useState('');
  const [pagina, setPagina] = useState(1);
  const [porPagina, setPorPagina] = useState(50);

  const fetchAgenda = useCallback(async () => {
    setLoading(true);
    setError('');

    try {
      const params = { page: pagina, per_page: porPagina };
      if (periodo) params.period = periodo.format('YYYY-MM');
      if (estado) params.estado = estado;
      if (busqueda.trim()) params.q = busqueda.trim();

      setDatos(await fiscalAdminService.getAgenda(params));
    } catch (err) {
      setError(err?.response?.data?.error || 'Error al cargar la agenda');
      console.error('Error fetching agenda:', err);
    } finally {
      setLoading(false);
    }
  }, [periodo, estado, busqueda, pagina, porPagina]);

  useEffect(() => {
    fetchAgenda();
  }, [fetchAgenda]);

  // Cualquier cambio de filtro vuelve a la página 1: quedarse en la 4 de un
  // resultado que ahora tiene 2 páginas muestra una tabla vacía.
  const cambiarFiltro = (aplicar) => {
    setPagina(1);
    aplicar();
  };

  const resumen = datos?.resumen || { total: 0, declaradas: 0, pendientes: 0, bloqueadas: 0 };

  const columns = [
    {
      title: 'Contribuyente',
      dataIndex: 'client_name',
      key: 'cliente',
      render: (nombre, fila) => (
        <Space direction="vertical" size={0}>
          <Text strong>{nombre || fila.tax_name}</Text>
          {fila.tax_name && fila.tax_name !== nombre && (
            <Text type="secondary" style={{ fontSize: 12 }}>{fila.tax_name}</Text>
          )}
        </Space>
      ),
    },
    {
      title: 'RFC',
      dataIndex: 'tax_id',
      key: 'rfc',
      render: (rfc) => <Tag color="geekblue">{rfc}</Tag>,
    },
    {
      title: 'Plan',
      dataIndex: 'plan_name',
      key: 'plan',
      render: (plan) => (plan ? <Tag color="blue">{plan}</Tag> : <Tag>Sin plan</Tag>),
    },
    {
      title: 'Estado',
      dataIndex: 'estado',
      key: 'estado',
      render: (valor, fila) => {
        const def = ESTADOS[valor] || { color: 'default', label: valor };
        const motivo = fila.motivo_bloqueo ? MOTIVOS[fila.motivo_bloqueo] : null;

        return (
          <Space direction="vertical" size={0}>
            <Tag color={def.color} icon={def.icon}>{def.label}</Tag>
            {motivo && (
              <Tooltip title={motivo.ayuda}>
                <Text type="secondary" style={{ fontSize: 12 }}>{motivo.texto}</Text>
              </Tooltip>
            )}
          </Space>
        );
      },
    },
    {
      title: 'Declaración',
      key: 'declaracion',
      render: (_, fila) => {
        if (!fila.declaracion) return <Text type="secondary">—</Text>;

        // Importe cero es la declaración presentada sin pago: su acuse existe
        // igual, solo no hay línea de captura que pagar.
        return (
          <Space direction="vertical" size={0}>
            <Text strong>
              {fila.declaracion.con_pago
                ? `$${fila.declaracion.amount.toLocaleString('es-MX', { minimumFractionDigits: 2 })}`
                : 'Sin pago'}
            </Text>
            <Text type="secondary" style={{ fontSize: 12 }}>
              {String(fila.declaracion.registrada_en || '').slice(0, 10)}
            </Text>
          </Space>
        );
      },
    },
    {
      title: '',
      key: 'accion',
      render: (_, fila) => (
        <Button type="primary" size="small" onClick={() => setSeleccion(fila)}>
          {fila.estado === 'declarada' ? 'Ver' : 'Trabajar'}
        </Button>
      ),
    },
  ];

  // Gestión de la empresa elegida, sobre la misma pantalla.
  if (seleccion) {
    return (
      <Space direction="vertical" size="middle" style={{ width: '100%' }}>
        <Space>
          <Button icon={<ArrowLeftOutlined />} onClick={() => setSeleccion(null)}>
            Volver a la agenda
          </Button>
          <Text strong>{seleccion.client_name}</Text>
          <Tag color="geekblue">{seleccion.tax_id}</Tag>
          <Tag color={(ESTADOS[seleccion.estado] || {}).color}>{datos?.periodo}</Tag>
        </Space>

        <FichaFiscal companyId={seleccion.company_id} />
      </Space>
    );
  }

  return (
    <Card>
      <Space direction="vertical" size="middle" style={{ width: '100%' }}>
        <div>
          <Title level={4} style={{ marginBottom: 4 }}>
            <CalendarOutlined /> Agenda de declaraciones
          </Title>
          <Text type="secondary">
            Trabajo del periodo <Text strong>{datos?.periodo || '…'}</Text>. Una fila por
            empresa: la declaración es por RFC, así que un contribuyente con dos empresas
            tiene dos declaraciones.
          </Text>
        </div>

        {error && <Alert type="error" message={error} showIcon />}

        <Row gutter={16}>
          <Col xs={12} md={6}>
            <Card size="small">
              <Statistic title="Total" value={resumen.total} />
            </Card>
          </Col>
          <Col xs={12} md={6}>
            <Card size="small">
              <Statistic
                title="Declaradas"
                value={resumen.declaradas}
                valueStyle={{ color: token.colorSuccess }}
              />
            </Card>
          </Col>
          <Col xs={12} md={6}>
            <Card size="small">
              <Statistic
                title="Pendientes"
                value={resumen.pendientes}
                valueStyle={{ color: token.colorWarning }}
              />
            </Card>
          </Col>
          <Col xs={12} md={6}>
            {/* Separadas de las pendientes a propósito: ningún esfuerzo del
                operador las desbloquea, hay que contactar al contribuyente. */}
            <Card size="small">
              <Tooltip title="Sin e.firma o sin autorización del contribuyente. No dependen de ti: hay que contactarlo.">
                <Statistic
                  title="Bloqueadas"
                  value={resumen.bloqueadas}
                  valueStyle={{ color: resumen.bloqueadas > 0 ? token.colorError : undefined }}
                />
              </Tooltip>
            </Card>
          </Col>
        </Row>

        <Space wrap>
          <DatePicker
            picker="month"
            placeholder="Periodo"
            value={periodo}
            onChange={(valor) => cambiarFiltro(() => setPeriodo(valor))}
            allowClear
          />

          <Select
            allowClear
            placeholder="Todos los estados"
            style={{ width: 180 }}
            value={estado}
            onChange={(valor) => cambiarFiltro(() => setEstado(valor || null))}
            options={[
              { value: 'pendiente', label: 'Pendientes' },
              { value: 'declarada', label: 'Declaradas' },
              { value: 'bloqueada', label: 'Bloqueadas' },
            ]}
          />

          <Input.Search
            allowClear
            prefix={<SearchOutlined />}
            placeholder="Buscar por RFC o razón social"
            defaultValue={busqueda}
            onSearch={(valor) => cambiarFiltro(() => setBusqueda(valor))}
            style={{ width: 320 }}
          />

          <Button icon={<ReloadOutlined />} onClick={fetchAgenda} loading={loading}>
            Actualizar
          </Button>
        </Space>

        <Table
          rowKey="company_id"
          loading={loading}
          dataSource={datos?.empresas || []}
          columns={columns}
          scroll={{ x: true }}
          // `current` sale del estado local, NO de la respuesta.
          //
          // Leyéndolo del servidor, un error dejaba la UI muerta: el catch
          // conserva la respuesta vieja (page 1) mientras el estado vale 2, así
          // que el paginador pintaba la 1 y volver a pulsar la 2 llamaba
          // setPagina(2) con el mismo valor — React descarta el render y no se
          // dispara ninguna petición.
          pagination={{
            current: pagina,
            pageSize: porPagina,
            total: datos?.paginacion?.total || 0,
            showSizeChanger: true,
            pageSizeOptions: ['20', '50', '100', '200'],
            showTotal: (t) => `${t} empresa${t === 1 ? '' : 's'}`,
            onChange: (p, tam) => {
              setPagina(p);
              setPorPagina(tam);
            },
          }}
          locale={{
            emptyText: (
              <Empty
                description={
                  estado || busqueda
                    ? 'Sin coincidencias con los filtros aplicados'
                    : 'No hay empresas en tu cartera para este periodo'
                }
                image={Empty.PRESENTED_IMAGE_SIMPLE}
              />
            ),
          }}
        />
      </Space>
    </Card>
  );
};

export default Agenda;
